/**
 * Chat panel (spec 07 FR-1, 08 §4).
 *
 * Pravý vysouvací panel dostupný odevšad. SSE streaming, viditelné tool-cally
 * („čtu metriky…"), volba modelu v hlavičce, cena u odpovědi a tlačítko
 * „uložit jako poznatek".
 *
 * Kontext stránky (aktivní placement/experiment) se posílá do promptu, aby se
 * uživatel nemusel ptát „a to na jaké pozici".
 *
 * Session se zakládá líně — až první otázkou. Backend chce `session_id` v cestě
 * (`POST /ai/chat/sessions/{id}/messages`), takže bez ní se nedá streamovat.
 */
import { useCallback, useRef, useState } from 'react';
import { useAiConfig, useCreateChatSession } from '@/api/queries';
import { streamChat } from '@/api/sse';
import { MarkdownView } from '@/components/MarkdownView';
import { formatUsd } from '@/lib/formatCz';
import { SaveInsightDialog } from './SaveInsightDialog';

/** Zpráva v panelu — lokální tvar, protože streamovaná odpověď vzniká postupně. */
interface PanelMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  model: string | null;
  costUsd: number | null;
  toolCalls: PanelToolCall[];
}

export interface PanelToolCall {
  name: string;
  label_cs: string;
  status: 'running' | 'done' | 'failed';
  detail?: string;
}

export interface ChatPanelProps {
  open: boolean;
  onClose: () => void;
  pageContext: Record<string, unknown>;
}

export function ChatPanel({ open, onClose, pageContext }: ChatPanelProps) {
  const { data: config } = useAiConfig();
  const createSession = useCreateChatSession();

  const [messages, setMessages] = useState<PanelMessage[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState('');
  const [toolCalls, setToolCalls] = useState<PanelToolCall[]>([]);
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [model, setModel] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saveTarget, setSaveTarget] = useState<PanelMessage | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const activeModel = model ?? config?.chat_model ?? '';

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    });
  }, []);

  const send = useCallback(async () => {
    const question = input.trim();
    if (!question || streaming) return;

    setMessages((prev) => [
      ...prev,
      { id: `local-${Date.now()}`, role: 'user', content: question, model: null, costUsd: null, toolCalls: [] },
    ]);
    setInput('');
    setStreaming(true);
    setStreamText('');
    setToolCalls([]);
    setError(null);
    scrollToBottom();

    // Session vzniká až tady — prázdný panel žádnou nezakládá.
    let activeSession = sessionId;
    if (activeSession === null) {
      try {
        const session = await createSession.mutateAsync({
          title: question.slice(0, 80),
          model: activeModel || undefined,
          page_context: pageContext,
        });
        activeSession = session.id;
        setSessionId(session.id);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Nepodařilo se založit konverzaci.');
        setStreaming(false);
        return;
      }
    }

    const controller = new AbortController();
    abortRef.current = controller;

    let accumulated = '';
    const collectedTools: PanelToolCall[] = [];

    await streamChat(
      activeSession,
      { question, model: activeModel || null, page_context: pageContext },
      {
        onEvent: (event) => {
          switch (event.type) {
            case 'text':
              accumulated += event.delta;
              setStreamText(accumulated);
              scrollToBottom();
              break;
            case 'tool_call':
              collectedTools.push({
                name: event.name,
                label_cs: event.label_cs ?? event.name,
                status: 'running',
                detail: event.detail,
              });
              setToolCalls([...collectedTools]);
              break;
            case 'tool_result': {
              const target = collectedTools.find((t) => t.name === event.name);
              if (target) {
                target.status = event.status;
                if (event.detail) target.detail = event.detail;
                setToolCalls([...collectedTools]);
              }
              break;
            }
            case 'done':
              setMessages((prev) => [
                ...prev,
                {
                  id: String(event.message_id ?? `msg-${Date.now()}`),
                  role: 'assistant',
                  content: accumulated,
                  model: event.model ?? activeModel,
                  costUsd: event.cost_usd ?? null,
                  toolCalls: collectedTools,
                },
              ]);
              setStreamText('');
              setToolCalls([]);
              scrollToBottom();
              break;
            case 'error':
              setError(event.message);
              break;
          }
        },
        onError: (err: Error) => setError(err.message),
        onClose: () => {
          setStreaming(false);
          abortRef.current = null;
        },
      },
      controller.signal,
    );
  }, [input, streaming, sessionId, activeModel, pageContext, scrollToBottom, createSession]);

  const stop = () => {
    abortRef.current?.abort();
    setStreaming(false);
  };

  if (!open) return null;

  return (
    <>
      <aside className="chat-panel" aria-label="AI chat">
        <header className="chat-panel__header">
          <div className="chat-panel__title">
            <strong>Chat nad daty</strong>
            <span className="chat-panel__context">
              {typeof pageContext.placement_id === 'string'
                ? `kontext: ${pageContext.placement_id}`
                : typeof pageContext.experiment_id === 'number'
                  ? `kontext: experiment ${pageContext.experiment_id}`
                  : 'bez kontextu stránky'}
            </span>
          </div>

          <div className="chat-panel__header-actions">
            <select
              className="chat-panel__model"
              value={activeModel}
              onChange={(e) => setModel(e.target.value)}
              title="Model, kterým se odpoví. Levnější pro rychlý dotaz, silnější na analýzu."
            >
              {(config?.models_offered ?? []).map((m) => (
                <option key={m.id} value={m.id} title={m.hint}>
                  {m.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="chat-panel__close"
              onClick={onClose}
              aria-label="Zavřít chat"
            >
              ✕
            </button>
          </div>
        </header>

        <div className="chat-panel__messages" ref={scrollRef}>
          {messages.length === 0 && !streaming ? (
            <div className="chat-panel__intro">
              <p>
                Ptej se na data i na poznatky. Odpovědi se opírají o metriky přes MCP
                nástroje — u čísel uvidíš, odkud jsou.
              </p>
              <ul>
                <li>„Jaké CTR měla pozice fixed na lifee minulý týden?“</li>
                <li>„Proč má B vyšší refresh CTR na lifee?“</li>
                <li>„Které pozice mají signál underperformer?“</li>
              </ul>
            </div>
          ) : null}

          {messages.map((message) => (
            <div key={message.id} className={`chat-msg chat-msg--${message.role}`}>
              {message.toolCalls.length > 0 ? <ToolCallList calls={message.toolCalls} /> : null}

              {message.role === 'assistant' ? (
                <MarkdownView markdown={message.content} />
              ) : (
                <p className="chat-msg__text">{message.content}</p>
              )}

              {message.role === 'assistant' ? (
                <div className="chat-msg__meta">
                  {message.model ? <span>{message.model}</span> : null}
                  {message.costUsd !== null ? <span>{formatUsd(message.costUsd)}</span> : null}
                  <button
                    type="button"
                    className="chat-msg__save"
                    onClick={() => setSaveTarget(message)}
                  >
                    Uložit jako poznatek
                  </button>
                </div>
              ) : null}
            </div>
          ))}

          {streaming ? (
            <div className="chat-msg chat-msg--assistant">
              {toolCalls.length > 0 ? <ToolCallList calls={toolCalls} /> : null}
              {streamText ? (
                <MarkdownView markdown={streamText} />
              ) : (
                <p className="chat-msg__thinking">Přemýšlím…</p>
              )}
            </div>
          ) : null}

          {error ? (
            <div className="chat-panel__error" role="alert">
              {error}
            </div>
          ) : null}
        </div>

        <form
          className="chat-panel__composer"
          onSubmit={(e) => {
            e.preventDefault();
            void send();
          }}
        >
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void send();
              }
            }}
            placeholder="Zeptej se na cokoli z dat… (Enter odešle, Shift+Enter nový řádek)"
            rows={2}
            disabled={streaming}
          />
          {streaming ? (
            <button type="button" className="btn btn--ghost btn--sm" onClick={stop}>
              Zastavit
            </button>
          ) : (
            <button type="submit" className="btn btn--primary btn--sm" disabled={!input.trim()}>
              Odeslat
            </button>
          )}
        </form>
      </aside>

      {saveTarget ? (
        <SaveInsightDialog
          content={saveTarget.content}
          onClose={() => setSaveTarget(null)}
        />
      ) : null}
    </>
  );
}

/** Průběh tool-callů — uživatel má vidět, že se sahá na reálná data. */
function ToolCallList({ calls }: { calls: PanelToolCall[] }) {
  return (
    <ul className="tool-calls">
      {calls.map((call, i) => (
        <li key={`${call.name}-${i}`} className={`tool-call tool-call--${call.status}`}>
          <span className="tool-call__spinner" aria-hidden="true" />
          <span className="tool-call__label">{call.label_cs || call.name}</span>
          {call.detail ? <span className="tool-call__detail">{call.detail}</span> : null}
        </li>
      ))}
    </ul>
  );
}
